import React, { memo, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { validators } from 'investira.sdk';

import {
  Stack,
  CircularProgress,
  TextField,
  InputAdornment,
  Icon,
  Typography,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  DialogContent,
  useMediaQuery
} from 'investira.react.components';
import utils from '../../../utils';
import withResponseHandling from '../../../hoc/withResponseHandling';
import {
  acPesquisasIndicadoresChanged,
  acPesquisasAtivosChanged,
  acPesquisasPessoasChanged,
  acPesquisasGestoresChanged,
  acPesquisasAdministradoresChanged,
  acPesquisasCarteirasChanged
} from '../../../store/actions';
import SearchAddAtivoItem from './SearchAddAtivoItem';
import SearchAddIndicadorItem from './SearchAddIndicadorItem';
import SearchAddPessoaItem from './SearchAddPessoaItem';
import SearchAddCarteiraItem from './SearchAddCarteiraItem';

/**
 * Renderiza o componente SearchAdd.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.pageIsLoading - Propriedade `pageIsLoading` do componente.
 * @param {*} props.clearOnClose - Propriedade `clearOnClose` do componente.
 * @param {*} props.responseErrorHandling - Propriedade `responseErrorHandling` do componente.
 * @param {*} props.onSearch - Propriedade `onSearch` do componente.
 * @param {*} props.onData - Propriedade `onData` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchAdd = memo(props => {
  const {
    params = {},
    service,
    onClick,
    pageIsLoading = false,
    placeholder,
    clearOnClose,
    store = true,
    variant = 'ativos' // indicadores | ativos | empresas | gestor | administrador | carteiras
  } = props;

  const xVariants = {
    indicadores: SearchAddIndicadorItem,
    ativos: SearchAddAtivoItem,
    empresas: SearchAddPessoaItem,
    gestores: SearchAddPessoaItem,
    administradores: SearchAddPessoaItem,
    carteiras: SearchAddCarteiraItem
  };

  const stored = {
    indicadores: { key: 'indicadores', action: acPesquisasIndicadoresChanged },
    ativos: { key: 'ativos', action: acPesquisasAtivosChanged },
    empresas: { key: 'empresas', action: acPesquisasPessoasChanged },
    gestores: { key: 'gestores', action: acPesquisasGestoresChanged },
    administradores: { key: 'administradores', action: acPesquisasAdministradoresChanged },
    carteiras: { key: 'carteiras', action: acPesquisasCarteirasChanged }
  };

  const SearchAddItem = xVariants[variant];

  const currentStored = stored[variant];

  const search = useSelector(store => store.pesquisas[currentStored.key]);

  const storeDispatch = useDispatch();

  const [isLoading, setRequesting] = useState(false);
  const [pesquisa, setPesquisa] = useState('');
  const [data, setData] = useState({});
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(null);

  const inputRef = useRef();
  const placeholderRef = useRef();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    placeholderRef.current.blur();
    inputRef.current.blur();
    handleClearPesquisa();
  };

  const handleChange = pEvent => {
    const xValue = pEvent.currentTarget.value;

    setPesquisa(pEvent.currentTarget.value);
    handleOpen(pEvent);

    if (validators.isEmpty(xValue)) {
      handleClose();
      return;
    }
  };

  const openDrawerOnFocus = pEvent => {
    placeholderRef.current.blur();
    handleOpen(pEvent);
  };

  const handleClearPesquisa = (pLocation = {}) => {
    setPesquisa('');
    setData({});
  };

  const handleAdd = (pEvent, pData, pClearOnClose) => {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    onClick && onClick(pData);

    handleClose();
    pClearOnClose && handleClearPesquisa();
  };

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, '');
    }

    return pStr;
  }

  const request = useMemo(() => {
    if (!service) {
      return console.error('Serviço de busca não definido.');
    }

    return utils.helpers.debounce(
      (pParams, pRes, pErr, pFin) => service(pParams, pRes, pErr, pFin),
      1000
    );
  }, []);

  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, ' ').trim();
  }

  function removeEmptyItems(pArr) {
    return pArr.filter(item => item !== '');
  }

  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(' ');
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map(xString => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join('');

    return xSearchFormated;
  }

  useEffect(() => {
    if (open) {
      placeholderRef.current.blur();
      inputRef.current.focus();

      store && setData(search.resultado);
    }
  }, [open, inputRef.current, placeholderRef.current]);

  useEffect(() => {
    if (pesquisa && pesquisa.length > 1 && !pageIsLoading) {
      setRequesting(true);
      setData({});
      setMessage(null);

      const xPesquisa = formatSearch(pesquisa);

      const xParams = {
        ...params,
        pesquisa: xPesquisa
      };

      request(
        {
          ...xParams
        },
        rRes => {
          setData(rRes.data);
          store &&
            currentStored &&
            storeDispatch(
              currentStored.action({
                pesquisa: pesquisa,
                resultado: rRes.data
                //pages: rRes.pages
              })
            );

          if (validators.isEmpty(rRes.data)) {
            setMessage('Nenhum dado encontrado');
          }
        },
        rErr => {
          setMessage('Erro na busca por empresa');
        },
        () => {
          setRequesting(false);
        }
      );
    }
  }, [pesquisa, request]);

  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: {
          sx: {
            pt: 3,
            pb: 2,
            px: 2
          }
        },
        maxWidth: 'lg'
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: {
            margin: 0,
            backgroundColor: 'background.dark'
          }
        }
      };

  return (
    <>
      <Stack
        id="search-area"
        sx={{
          position: 'relative',
          width: '100%',
          zIndex: '999'
        }}>
        {pageIsLoading ? (
          <Skeleton variant="rounded" width="100%" height={56} />
        ) : (
          <TextField
            id="placeholder-busca-empresa"
            inputRef={placeholderRef}
            value={pesquisa}
            onFocus={openDrawerOnFocus}
            variant="outlined"
            placeholder={placeholder || 'Pesquise por ativos'}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="find" size={21} color={'primary'} />
                </InputAdornment>
              )
            }}
            disabled={pageIsLoading}
          />
        )}

        <Dialog {...xDialogProps} open={open} onClose={handleClose} keepMounted fullWidth>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack>
                <IconButton onClick={handleClose} color={'primary'}>
                  <Icon iconName="arrow_left" size={21} />
                </IconButton>
              </Stack>
              <Stack flexGrow={1}>
                <TextField
                  id="busca-empresa"
                  inputRef={inputRef}
                  value={pesquisa}
                  onChange={handleChange}
                  sx={{ border: 'none' }}
                  variant="outlined"
                  size={isUpMd ? 'medium' : 'small'}
                  placeholder={'Pesquise por fundos'}
                  fullWidth
                  disabled={pageIsLoading}
                />
              </Stack>
            </Stack>
            <Divider />
            <Stack>
              {!isLoading && !validators.isEmpty(data) && (
                <>
                  <Stack id="busca-empresa-list" sx={{ px: 0 }} component="ul">
                    {Object.values(data).map((xData, xIndex) => {
                      return (
                        <SearchAddItem
                          key={`busca-empresa-list-${xIndex}`}
                          data={xData}
                          onClick={e => handleAdd(e, xData, clearOnClose)}
                        />
                      );
                    })}
                  </Stack>
                </>
              )}

              {isLoading && (
                <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}

              {!isLoading && validators.isEmpty(data) && (
                <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{ py: 2 }}>
                  <Typography variant="body2" color="textPrimary">
                    {message}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      </Stack>
    </>
  );
});

export default withResponseHandling(SearchAdd);
